import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import { LogOut, Euro, Star, Receipt } from "lucide-react";

export default function Tracker() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const [partnerData, setPartnerData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // 1. Fetch Partner
    const { data: partner, error: pError } = await supabase
      .from("partners")
      .select("*")
      .eq("code", partnerCode)
      .single();

    if (pError || !partner) {
      setError("Invalid Code");
      setLoading(false);
      return;
    }

    setPartnerData(partner);
    setIsAuthenticated(true);
    
    // 2. Fetch Transactions (The logic that worked!)
    const { data: trans, error: tError } = await supabase
      .from("transactions")
      .select("id, amount, created_at, points_awarded, note")
      .eq("partner_id", partner.id)
      .order("created_at", { ascending: false });

    if (tError) {
      setError("Fetch failed");
    } else {
      setTransactions(trans || []);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPartnerCode("");
    setPartnerData(null);
    setTransactions([]);
  };

  const styles = {
    container: { 
      minHeight: "100vh", 
      backgroundColor: "#F3F5F3", 
      padding: "2rem 1.5rem", 
      fontFamily: "'Inter', sans-serif" 
    },
    loginCard: { 
      maxWidth: "400px", 
      margin: "100px auto", 
      backgroundColor: "white", 
      padding: "2.5rem", 
      borderRadius: "32px", 
      textAlign: "center", 
      boxShadow: "0 20px 40px rgba(0,0,0,0.05)" 
    },
    input: { 
      width: "100%", 
      padding: "1rem", 
      margin: "1rem 0", 
      borderRadius: "16px", 
      border: "1.5px solid #E2E8F0", 
      fontSize: "1.2rem", 
      textAlign: "center", 
      fontWeight: "800", 
      letterSpacing: "4px",
      boxSizing: "border-box"
    },
    btn: { 
      width: "100%", 
      padding: "1.1rem", 
      borderRadius: "18px", 
      border: "none", 
      background: "linear-gradient(90deg, #12873B 0%, #2D302D 100%)", 
      color: "white", 
      fontWeight: "800", 
      cursor: "pointer",
      boxShadow: "0 10px 20px rgba(18, 135, 59, 0.2)"
    },
    statCard: { 
      backgroundColor: "white", 
      padding: "1.5rem", 
      borderRadius: "24px", 
      flex: 1, 
      boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
      display: "flex",
      flexDirection: "column",
      gap: "5px"
    },
    table: { 
      width: "100%", 
      backgroundColor: "white", 
      borderRadius: "28px", 
      overflow: "hidden", 
      borderCollapse: "collapse",
      marginTop: "1rem"
    },
    th: { 
      padding: "1.2rem 1rem", 
      backgroundColor: "#F8FAFB", 
      textAlign: "left", 
      color: "#94A3B8", 
      fontWeight: "700", 
      fontSize: "0.85rem",
      textTransform: "uppercase"
    },
    td: { 
      padding: "1.2rem 1rem", 
      borderBottom: "1px solid #F1F5F1", 
      fontSize: "0.95rem" 
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <img src="https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/faculty%20logos/humming_logo.png" alt="Humming" style={{ width: "60px", marginBottom: "1rem" }} />
          <h2 style={{ fontWeight: "900", color: "#2D302D", marginBottom: "0.5rem" }}>Partner Portal</h2>
          <p style={{ color: "#64748B", fontSize: "0.9rem", marginBottom: "1.5rem" }}>Enter your terminal code to see sales.</p>
          <form onSubmit={handleLogin}>
            <input 
              type="password" 
              value={partnerCode} 
              onChange={(e) => setPartnerCode(e.target.value)} 
              placeholder="••••" 
              style={styles.input} 
              required
            />
            <button type="submit" style={styles.btn}>
              {loading ? "Verifying..." : "View Dashboard"}
            </button>
            {error && <p style={{ color: "#E53E3E", fontSize: "0.85rem", marginTop: "15px", fontWeight: "600" }}>{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ maxWidth: "850px", margin: "0 auto" }}>
        
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2.5rem" }}>
          <div>
            <h1 style={{ margin: 0, fontWeight: "900", fontSize: "2rem", color: "#2D302D" }}>{partnerData.name}</h1>
            <p style={{ margin: 0, color: "#12873B", fontWeight: "700", fontSize: "1rem" }}>Insights & Transactions</p>
          </div>
          <button onClick={handleLogout} style={{ ...styles.btn, width: "auto", padding: "0.8rem", borderRadius: "14px" }}>
            <LogOut size={20} />
          </button>
        </div>

        {/* Stats Grid */}
        <div style={{ display: "flex", gap: "1.5rem", marginBottom: "2.5rem", flexWrap: "wrap" }}>
          <div style={styles.statCard}>
            <Receipt size={22} color="#12873B" />
            <div style={{ fontSize: "1.8rem", fontWeight: "900" }}>{transactions.length}</div>
            <div style={{ fontSize: "0.8rem", color: "#94A3B8", fontWeight: "600" }}>Total Sales</div>
          </div>
          <div style={styles.statCard}>
            <Euro size={22} color="#12873B" />
            <div style={{ fontSize: "1.8rem", fontWeight: "900" }}>
              €{transactions.reduce((acc, t) => acc + (Number(t.amount) || 0), 0).toFixed(2)}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#94A3B8", fontWeight: "600" }}>Total Revenue</div>
          </div>
          <div style={styles.statCard}>
            <Star size={22} color="#12873B" />
            <div style={{ fontSize: "1.8rem", fontWeight: "900" }}>
              {transactions.reduce((acc, t) => acc + (t.points_awarded || 0), 0)}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#94A3B8", fontWeight: "600" }}>Points Issued</div>
          </div>
        </div>

        {/* Transaction Table */}
        <h3 style={{ fontWeight: "900", color: "#2D302D", marginBottom: "1rem", fontSize: "1.2rem" }}>Recent Activity</h3>
        <div style={{ overflowX: "auto", borderRadius: "28px" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Purchase Description</th>
                <th style={styles.th}>Date</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((t) => (
                  <tr key={t.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: "700", color: "#2D302D" }}>{t.note || "Product Sale"}</div>
                      <div style={{ fontSize: "0.75rem", color: "#12873B", fontWeight: "600" }}>+{t.points_awarded} Points awarded</div>
                    </td>
                    <td style={{ ...styles.td, color: "#94A3B8", fontWeight: "500" }}>
                      {new Date(t.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: "900", color: "#2D302D", fontSize: "1.1rem" }}>
                      €{Number(t.amount).toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ ...styles.td, textAlign: "center", color: "#94A3B8", padding: "3rem" }}>
                    No transactions found for this partner yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}